/**
 * useEnvironments Hook
 *
 * Convenience hook that wraps EnvironmentContext and provides
 * a consistent interface for environment selection components
 */

import { useTenant } from '@/features/tenants'
import { toast } from '@/lib/toast'
import type { Environment } from '@/shared/components/common/EnvironmentSelector'
import { useEnvironment } from '../contexts/EnvironmentContext'
import { environmentService } from '../services/environmentService'

export function useEnvironments() {
  const {
    currentEnvironment,
    environments,
    isLoadingEnvironments,
    environmentError,
    switchEnvironment,
    refreshEnvironments,
  } = useEnvironment()
  const { currentTenant } = useTenant()

  const handleEnvironmentChange = async (environment: Environment) => {
    if (environment.id) {
      await switchEnvironment(environment.id)
    }
  }

  const handleAddEnvironment = async (
    name: string,
    type: Environment['type']
  ) => {
    try {
      // Validate tenant is selected
      if (!currentTenant?.id) {
        toast.error('No tenant selected')
        return
      }

      // Generate slug from environment name
      const slug = environmentService.generateSlug(name)

      // Create environment
      const newEnvironment = await environmentService.createEnvironment({
        tenant_id: currentTenant.id,
        name,
        type,
        slug,
      })

      // Automatically switch to the newly created environment
      await switchEnvironment(newEnvironment.id)

      toast.success('Environment created successfully')
    } catch {
      toast.error('Failed to create environment')
    }
  }

  return {
    currentEnvironment,
    environments,
    isLoading: isLoadingEnvironments,
    error: environmentError,
    onEnvironmentChange: handleEnvironmentChange,
    onAddEnvironment: handleAddEnvironment,
    onRetry: refreshEnvironments,
  }
}
